// src/components/modals/AddPaymentModal.tsx

import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { ModalCancelButton, ModalSaveButton } from '../common/Buttons';
import CustomSelect from '../common/CustomSelect';
import CustomSelectMenuItem from '../common/CustomSelectMenuItem';
import InfoTooltip from '../common/InfoTooltip';
import FieldHelperText from '../forms/shared/FieldHelperText';
import DateRangePicker from '../forms/shared/DateRangePicker';
import {
  StyledTextField,
  StyledMultilineTextField,
  RadioButtonGroup,
} from '../forms/shared';
import ModalBase from '../common/modal/ModalBase';
import {
  paymentAPI,
  InvoiceSummary,
  CreatePaymentRequest,
} from '../../services/api';

interface AddPaymentModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  invoices: InvoiceSummary[];
  clientId: number;
  preselectedInvoiceId?: number;
}

const PAYMENT_METHODS = [
  { value: 'EFT', label: 'EFT' },
  { value: 'Card', label: 'Card' },
  { value: 'Cash', label: 'Cash' },
  { value: 'Deposit', label: 'Deposit' },
  { value: 'Credit', label: 'Credit' },
  { value: 'Refund', label: 'Refund' },
  { value: 'Journal', label: 'Journal' },
  { value: 'OpeningBalance', label: 'Opening Balance' },
];

// Settlement methods need an invoice; adjustment methods can stand alone.
// Mirrors PaymentController's server-side rule.
const ADJUSTMENT_METHODS = ['Credit', 'Refund', 'Journal', 'OpeningBalance'];

// Journal and OpeningBalance can go either way, so the user picks the direction.
const BIDIRECTIONAL_METHODS = ['Journal', 'OpeningBalance'];

const DIRECTION_OPTIONS = [
  { value: 'credit', label: 'Credit' },
  { value: 'debit', label: 'Debit' },
];

// Sentinel for the invoice select meaning "no invoice".
const NO_INVOICE_VALUE = 'none';

interface FieldErrors {
  invoice?: string;
  amount?: string;
  paymentMethod?: string;
  reference?: string;
  direction?: string;
}

const FieldLabel = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '0.8rem',
  marginBottom: theme.spacing(0.2),
}));

const FieldLabelText = styled(Typography)(({ theme }) => ({
  fontSize: '1.9rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  '& .required': {
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.grey[200]
        : theme.palette.secondary.main,
    marginLeft: '4px',
  },
}));

const FooterButtons = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  justifyContent: 'center',
}));

const REFERENCE_HELP_TEXT =
  'A short identifier for this transaction - e.g. an EFT confirmation number, cheque number, or receipt number. Used to match this payment against your bank statement later.';

const DESCRIPTION_HELP_TEXT =
  'Optional extra context for this entry - e.g. why a payment or adjustment was made. Shown to staff reviewing the ledger, not sent to the client.';

const AddPaymentModal: React.FC<AddPaymentModalProps> = ({
  open,
  onClose,
  onSuccess,
  invoices,
  clientId,
  preselectedInvoiceId,
}) => {
  const [invoiceSelection, setInvoiceSelection] = useState<string>(
    preselectedInvoiceId ? String(preselectedInvoiceId) : ''
  );
  const [amount, setAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [paymentMethod, setPaymentMethod] = useState('');
  const [direction, setDirection] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [apiError, setApiError] = useState('');

  useEffect(() => {
    if (open) {
      setInvoiceSelection(
        preselectedInvoiceId ? String(preselectedInvoiceId) : ''
      );
    }
  }, [open, preselectedInvoiceId]);

  const isAdjustment = ADJUSTMENT_METHODS.includes(paymentMethod);
  const needsDirection = BIDIRECTIONAL_METHODS.includes(paymentMethod);
  const isNoInvoice = invoiceSelection === NO_INVOICE_VALUE;
  const selectedInvoiceId =
    !isNoInvoice && invoiceSelection ? Number(invoiceSelection) : null;
  const selectedInvoice = selectedInvoiceId
    ? invoices.find((i) => i.id === selectedInvoiceId)
    : undefined;

  // Cancelled invoices are always disabled (the backend rejects them for every
  // method); Paid ones are only selectable for adjustment methods.
  const trulyNoOptions = invoices.length === 0 && !isAdjustment;

  const clearFieldError = (field: keyof FieldErrors) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const resetFormFields = () => {
    setInvoiceSelection('');
    setAmount('');
    setPaymentMethod('');
    setDirection('');
    setReference('');
    setNotes('');
  };

  const handleClose = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setFieldErrors({});
    setApiError('');
    resetFormFields();
    onClose();
  };

  const validate = (): boolean => {
    const errors: FieldErrors = {};

    if (!paymentMethod) {
      errors.paymentMethod = 'Please select a payment method';
    }
    if (!invoiceSelection) {
      errors.invoice = 'Please select an invoice';
    }
    const parsedAmount = parseFloat(amount);
    if (!amount || isNaN(parsedAmount) || parsedAmount <= 0) {
      errors.amount = 'Please enter a valid amount';
    }
    if (!reference.trim()) {
      errors.reference = 'Please enter a reference';
    }
    if (needsDirection && !direction) {
      errors.direction = 'Please specify whether this is a debit or credit';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setSaving(true);
    setApiError('');
    try {
      const parsedAmount = parseFloat(amount);
      await paymentAPI.create({
        invoiceId: isNoInvoice ? null : selectedInvoiceId,
        clientId: isNoInvoice ? clientId : undefined,
        amount: parsedAmount,
        paymentDate,
        paymentMethod: paymentMethod as CreatePaymentRequest['paymentMethod'],
        isDebit: needsDirection ? direction === 'debit' : undefined,
        transactionReference: reference || undefined,
        notes: notes || undefined,
      });

      onSuccess();
      handleClose();
    } catch (err: any) {
      const data = err?.response?.data;
      setApiError(
        (typeof data === 'string' && data) ||
          data?.error ||
          'Failed to record payment'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalBase
      open={open}
      onClose={handleClose}
      headerIcon={<AddIcon />}
      headerTitle="Add Payment"
      mobileWidth="92vw"
      maxHeight="600px"
      footer={
        <FooterButtons>
          <ModalCancelButton onClick={handleClose} disabled={saving} />
          <ModalSaveButton
            onClick={handleSubmit}
            isSubmitting={saving}
            text={saving ? 'Saving...' : 'Save'}
          />
        </FooterButtons>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        <CustomSelect
          label=""
          ariaLabel="Invoice"
          value={invoiceSelection}
          onChange={(val) => {
            setInvoiceSelection(val);
            clearFieldError('invoice');
          }}
          error={!!fieldErrors.invoice}
          helperText={fieldErrors.invoice}
          placeholder="Select invoice..."
          required
        >
          {trulyNoOptions ? (
            <CustomSelectMenuItem value="" disabled>
              No invoices available
            </CustomSelectMenuItem>
          ) : (
            [
              ...(isAdjustment
                ? [
                    <CustomSelectMenuItem
                      key="no-invoice"
                      value={NO_INVOICE_VALUE}
                    >
                      No Invoice
                    </CustomSelectMenuItem>,
                  ]
                : []),
              ...invoices.map((inv) => {
                const isCancelled = inv.status === 'Cancelled';
                const isPaid = inv.status === 'Paid';
                const disabled = isCancelled || (isPaid && !isAdjustment);
                const suffix = isCancelled
                  ? ' (Cancelled)'
                  : isPaid
                    ? ' (Paid)'
                    : '';
                return (
                  <CustomSelectMenuItem
                    key={inv.id}
                    value={String(inv.id)}
                    disabled={disabled}
                  >
                    {inv.invoiceNumber} — R{inv.outstandingAmount.toFixed(2)}{' '}
                    outst.{suffix}
                  </CustomSelectMenuItem>
                );
              }),
            ]
          )}
        </CustomSelect>

        <CustomSelect
          label=""
          ariaLabel="Payment method"
          value={paymentMethod}
          onChange={(val) => {
            setPaymentMethod(val);
            clearFieldError('paymentMethod');
            if (!BIDIRECTIONAL_METHODS.includes(val)) {
              setDirection('');
              clearFieldError('direction');
            }
          }}
          error={!!fieldErrors.paymentMethod}
          helperText={fieldErrors.paymentMethod}
          placeholder="Select method..."
          required
        >
          {PAYMENT_METHODS.map((m) => (
            <CustomSelectMenuItem key={m.value} value={m.value}>
              {m.label}
            </CustomSelectMenuItem>
          ))}
        </CustomSelect>

        {needsDirection && (
          <Box>
            <FieldLabel>
              <FieldLabelText>
                Direction<span className="required">*</span>
              </FieldLabelText>
            </FieldLabel>
            <RadioButtonGroup
              value={direction}
              onChange={(val) => {
                setDirection(val);
                clearFieldError('direction');
              }}
              options={DIRECTION_OPTIONS}
              error={!!fieldErrors.direction}
              helperText={fieldErrors.direction}
            />
            <Typography
              sx={{
                mt: 0.5,
                pl: 3,
                textAlign: 'left',
                fontSize: '1.9rem',
                color: 'text.secondary',
              }}
            >
              {direction === 'credit'
                ? 'lowers balance'
                : direction === 'debit'
                  ? 'raises balance'
                  : ''}
            </Typography>
          </Box>
        )}

        <Box>
          <FieldLabel>
            <FieldLabelText>
              Amount (R)<span className="required">*</span>
            </FieldLabelText>
          </FieldLabel>
          <StyledTextField
            type="number"
            value={amount}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setAmount(e.target.value);
              clearFieldError('amount');
            }}
            fullWidth
            inputProps={{ min: 0.01, step: 0.01 }}
            error={!!fieldErrors.amount}
            helperText={
              fieldErrors.amount
                ? fieldErrors.amount
                : selectedInvoice
                  ? `Outstanding: R ${selectedInvoice.outstandingAmount.toFixed(2)}`
                  : undefined
            }
          />
        </Box>

        <Box>
          <FieldLabel>
            <FieldLabelText>
              Payment Date<span className="required">*</span>
            </FieldLabelText>
          </FieldLabel>
          <DateRangePicker
            mode="single"
            selectedDate={paymentDate}
            onDateChange={(date) => setPaymentDate(date)}
          />
        </Box>

        <Box>
          <FieldLabel>
            <FieldLabelText>
              Reference<span className="required">*</span>
            </FieldLabelText>
            <InfoTooltip text={REFERENCE_HELP_TEXT} />
          </FieldLabel>
          <StyledTextField
            value={reference}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setReference(e.target.value);
              clearFieldError('reference');
            }}
            fullWidth
            error={!!fieldErrors.reference}
            helperText={fieldErrors.reference}
            inputProps={{ maxLength: 100 }}
          />
        </Box>

        <Box sx={{ mb: '1rem' }}>
          <FieldLabel>
            <FieldLabelText>Description</FieldLabelText>
            <InfoTooltip text={DESCRIPTION_HELP_TEXT} />
          </FieldLabel>
          <StyledMultilineTextField
            value={notes}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setNotes(e.target.value)
            }
            fullWidth
            multiline
            rows={2}
            inputProps={{ maxLength: 150 }}
          />
        </Box>

        {apiError && <FieldHelperText error={true} helperText={apiError} />}
      </Box>
    </ModalBase>
  );
};

export default AddPaymentModal;
